import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Plus, Edit, Trash, Eye, EyeOff, Calendar, Clock, Mail, Phone, Building, MessageSquare, AlertCircle, Tag, FileText, LayoutGrid, Columns3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import backgroundNoise from "@/assets/background-noise.svg";
import logo from "@/assets/logo.svg";
import { LeadDetailDialog } from "@/components/LeadDetailDialog";
import { LeadKanbanBoard } from "@/components/LeadKanbanBoard";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  category: string;
  published: boolean;
  created_at: string;
  author_name: string;
  read_time: string;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  message: string;
  status: string;
  priority: string;
  source: string;
  notes: string | null;
  tags: string[] | null;
  last_contact_date: string | null;
  next_follow_up_date: string | null;
  created_at: string;
  updated_at: string;
}

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuth();
    fetchPosts();
    fetchLeads();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate('/admin');
      return;
    }

    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .maybeSingle();

    if (!roles) {
      navigate('/admin');
    }
  };

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch blog posts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLeads(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch leads",
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Post deleted successfully",
      });
      fetchPosts();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const togglePublish = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ published: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Post ${!currentStatus ? 'published' : 'unpublished'} successfully`,
      });
      fetchPosts();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteLead = async (id: string) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;

    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });
      fetchLeads();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateLeadStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead status updated successfully",
      });
      fetchLeads();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-background"
      style={{
        backgroundImage: `url(${backgroundNoise})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Clean Professional Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-5">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2 sm:gap-4">
              <img 
                src={logo} 
                alt="Logo" 
                className="h-8 sm:h-10 w-auto brightness-0"
                loading="lazy"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout} 
              className="border-border font-medium"
              size="sm"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 sm:py-8">
        <Tabs defaultValue="blog" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-4 sm:mb-8">
            <TabsTrigger value="blog" className="text-xs sm:text-sm">Blog Posts</TabsTrigger>
            <TabsTrigger value="leads" className="text-xs sm:text-sm">Leads ({leads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="blog">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-foreground">Blog Posts</h2>
                <p className="text-sm text-muted-foreground">Manage your blog content</p>
              </div>
              <Button 
                onClick={() => navigate('/admin/post/new')}
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md w-full sm:w-auto"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>

            {posts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-accent/10 flex items-center justify-center">
                    <Plus className="w-10 h-10 text-accent" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-foreground">No blog posts yet</h3>
                  <p className="text-muted-foreground mb-8 text-lg">Create your first post to get started</p>
                  <Button 
                    onClick={() => navigate('/admin/post/new')}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md"
                    size="lg"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create First Post
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 sm:gap-6">
                {posts.map((post) => (
                  <Card key={post.id} className="transition-all duration-300 group">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                            <Badge 
                              variant={post.published ? "default" : "secondary"}
                              className={`text-xs ${post.published ? "bg-accent text-accent-foreground font-semibold" : "font-semibold"}`}
                            >
                              {post.published ? "Published" : "Draft"}
                            </Badge>
                            <Badge variant="outline" className="font-medium text-xs">{post.category}</Badge>
                          </div>
                          <h3 className="text-base sm:text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 sm:gap-5 text-xs sm:text-sm text-muted-foreground">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">{new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                              <span className="sm:hidden">{new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>{post.read_time}</span>
                            </div>
                            <span className="hidden sm:inline">by {post.author_name}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/post/${post.id}`)}
                          className="hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all font-medium text-xs flex-1 sm:flex-none"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => togglePublish(post.id, post.published)}
                          className="hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all font-medium text-xs flex-1 sm:flex-none"
                        >
                          {post.published ? <EyeOff className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" /> : <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />}
                          <span className="hidden sm:inline">{post.published ? "Unpublish" : "Publish"}</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          className="hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all font-medium text-xs flex-1 sm:flex-none"
                        >
                          <Trash className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="leads">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">Lead Management</h2>
                  <p className="text-sm text-muted-foreground">Track and manage your business leads</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  >
                    <LayoutGrid className="w-4 h-4 mr-2" />
                    Grid
                  </Button>
                  <Button
                    variant={viewMode === 'kanban' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('kanban')}
                  >
                    <Columns3 className="w-4 h-4 mr-2" />
                    Kanban
                  </Button>
                </div>
              </div>

              {/* Stats Overview */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card className="glass border-primary/20">
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">{leads.length}</div>
                    <div className="text-xs sm:text-sm text-muted-foreground">Total Leads</div>
                  </CardContent>
                </Card>
                <Card className="glass border-accent/20">
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-2xl sm:text-3xl font-bold text-accent mb-1">
                      {leads.filter(l => l.status === "new").length}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">New Leads</div>
                  </CardContent>
                </Card>
                <Card className="glass border-destructive/20">
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-2xl sm:text-3xl font-bold text-destructive mb-1">
                      {leads.filter(l => l.priority === "high").length}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">High Priority</div>
                  </CardContent>
                </Card>
                <Card className="glass border-primary/20">
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="text-2xl sm:text-3xl font-bold text-primary mb-1">
                      {leads.filter(l => l.next_follow_up_date && new Date(l.next_follow_up_date) <= new Date()).length}
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">Due Today</div>
                  </CardContent>
                </Card>
              </div>

              {/* View Mode: Kanban or Grid */}
              {leads.length === 0 ? (
                <Card className="glass border-border rounded-3xl">
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-accent/10 flex items-center justify-center">
                      <MessageSquare className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">No leads yet</h3>
                    <p className="text-muted-foreground text-lg">New inquiries will appear here</p>
                  </CardContent>
                </Card>
              ) : viewMode === 'kanban' ? (
                <LeadKanbanBoard
                  leads={leads}
                  onStatusChange={updateLeadStatus}
                  onLeadClick={(lead) => {
                    setSelectedLead(lead);
                    setLeadDialogOpen(true);
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {leads.map((lead) => (
                    <Card 
                      key={lead.id}
                      className="glass border-border rounded-3xl cursor-pointer group"
                      onClick={() => { setSelectedLead(lead); setLeadDialogOpen(true); }}
                    >
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg sm:text-xl font-bold mb-1 group-hover:text-primary transition-colors">
                              {lead.name}
                            </h3>
                            {lead.company && (
                              <p className="text-xs sm:text-sm text-muted-foreground font-medium flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {lead.company}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <Badge
                              variant="outline"
                              className={
                                lead.priority === "high"
                                  ? "border-destructive bg-destructive/10 text-destructive"
                                  : lead.priority === "medium"
                                  ? "border-yellow-500 bg-yellow-500/10 text-yellow-600"
                                  : "border-green-500 bg-green-500/10 text-green-600"
                              }
                            >
                              {lead.priority === "high" && <AlertCircle className="w-3 h-3 mr-1" />}
                              {lead.priority}
                            </Badge>
                            <Badge
                              className={
                                lead.status === "new"
                                  ? "bg-accent text-accent-foreground"
                                  : "bg-secondary text-secondary-foreground"
                              }
                            >
                              {lead.status}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                            <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <a 
                              href={`mailto:${lead.email}`} 
                              className="text-primary hover:underline break-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {lead.email}
                            </a>
                          </div>
                          {lead.phone && (
                            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                              <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <a 
                                href={`tel:${lead.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-primary"
                              >
                                {lead.phone}
                              </a>
                            </div>
                          )}
                          
                          {lead.next_follow_up_date && (
                            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                              <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                              <span>
                                <span className="text-muted-foreground">Follow-up: </span>
                                <span className="font-medium">
                                  {format(new Date(lead.next_follow_up_date), "MMM dd, yyyy")}
                                </span>
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                            <Badge variant="secondary" className="text-xs">
                              {lead.source}
                            </Badge>
                            <span className="text-muted-foreground text-xs">
                              {format(new Date(lead.created_at), "MMM dd, yyyy")}
                            </span>
                          </div>
                        </div>

                        <div className="mb-4 pb-4 border-t pt-4 border-border">
                          <div className="flex items-start gap-2 text-xs sm:text-sm">
                            <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <p className="text-muted-foreground line-clamp-2">{lead.message}</p>
                          </div>
                        </div>

                        {lead.tags && lead.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 sm:gap-2 mb-4 pb-4 border-t pt-4 border-border">
                            {lead.tags.map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                <Tag className="w-3 h-3 mr-1" />
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}

                        <div className="flex gap-2 sm:gap-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => { setSelectedLead(lead); setLeadDialogOpen(true); }}
                            className="flex-1 bg-accent text-accent-foreground text-xs sm:text-sm"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteLead(lead.id)}
                            className="text-xs sm:text-sm"
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Lead Detail Dialog */}
      <LeadDetailDialog
        lead={selectedLead}
        open={leadDialogOpen}
        onOpenChange={setLeadDialogOpen}
        onUpdate={fetchLeads}
      />
    </div>
  );
};

export default AdminDashboard;
